import {
    createSimpleGetter,
    createSimpleMutator,
} from '@superbet-group/web.lib.vue-utilities';
import {
    keyBy as _keyBy,
    groupBy as _groupBy,
    orderBy as _orderBy,
} from 'lodash';
import Vue from 'vue';
import { Commit } from 'vuex';

import {
    Article,
    ArticlePreview,
} from '@core/models/news/Article';
import OfferEvent from '@core/models/offer/OfferEvent';
import { newsService } from '@core/services/news/NewsService';
import { asyncErrorGuard } from '@core/utils/ErrorGuard';
import parseDate from '@core/utils/url/parseDateUrl';
import slugifyDate from '@core/utils/url/slugifyDate';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { isSocialEnabled } from '@socialFederation/helpers';

const SET_ARTICLES = 'SET_ARTICLES';
const SET_FULL_ARTICLE = 'SET_FULL_ARTICLE';
const SET_IS_LOADING = 'SET_IS_LOADING';
const SET_ARE_FIRST_ARTICLES_FETCHED = 'SET_ARE_FIRST_ARTICLES_FETCHED';
const SET_ARE_ALL_ARTICLES_FETCHED = 'SET_ARE_ALL_ARTICLES_FETCHED';
const SET_IS_SLUG_NEWS_FETCHED = 'SET_IS_SLUG_NEWS_FETCHED';
const SET_IS_SOCIAL_ANALYSES_ENABLED = 'SET_IS_SOCIAL_ANALYSES_ENABLED';

export type NewsState = {
    articles: Record<string, ArticlePreview>;
    fullArticles: Record<string, Article>;
    areArticlesLoading: boolean;
    areFirstArticlesFetched: boolean;
    areAllArticlesFetched: boolean;
    isSlugNewsFetched: boolean;
    areSocialAnalysesEnabled: boolean;
};

export default {
    namespaced: true,

    state: {
        articles: {},
        fullArticles: {},
        areArticlesLoading: false,
        areFirstArticlesFetched: false,
        areAllArticlesFetched: false,
        isSlugNewsFetched: false,
        areSocialAnalysesEnabled: false,
    } as NewsState,

    mutations: {
        [SET_ARTICLES](state: NewsState, articles: ArticlePreview[]) {
            articles.forEach((article) => {
                // These articles are experimental and should not be displayed on the Web
                // unless the user somehow access it directly via slug (hence SET_FULL_ARTICLE supports this)
                if (article.socialUserId != null) {
                    return;
                }

                Vue.set(state.articles, article.id, article);
            });
        },
        [SET_FULL_ARTICLE](state: NewsState, article: Article) {
            Vue.set(state.fullArticles, article.id, article);
        },
        [SET_IS_LOADING]: createSimpleMutator<NewsState, 'areArticlesLoading'>('areArticlesLoading'),
        [SET_ARE_FIRST_ARTICLES_FETCHED]: createSimpleMutator<NewsState, 'areFirstArticlesFetched'>('areFirstArticlesFetched'),
        [SET_ARE_ALL_ARTICLES_FETCHED]: createSimpleMutator<NewsState, 'areAllArticlesFetched'>('areAllArticlesFetched'),
        [SET_IS_SLUG_NEWS_FETCHED]: createSimpleMutator<NewsState, 'isSlugNewsFetched'>('isSlugNewsFetched'),
        [SET_IS_SOCIAL_ANALYSES_ENABLED]: createSimpleMutator<NewsState, 'areSocialAnalysesEnabled'>('areSocialAnalysesEnabled'),
    },

    actions: {
        fetchNews: asyncErrorGuard(async ({ commit }: { commit: Commit }) => {
            commit(SET_IS_LOADING, true);
            const articlesData = await newsService.getNextArticles();
            commit(SET_ARE_ALL_ARTICLES_FETCHED, articlesData.isDone);
            commit(SET_ARTICLES, articlesData.articles);
            commit(SET_ARE_FIRST_ARTICLES_FETCHED, true);
            commit(SET_IS_LOADING, false);
        }),
        refreshNews: asyncErrorGuard(async ({ commit }: { commit: Commit }) => {
            const articles = await newsService.getFirstArticles();
            commit(SET_ARTICLES, articles);
        }),
        getArticlesByMatchId: asyncErrorGuard(async ({ commit }: { commit: Commit }, matchId: OfferEvent['id']) => {
            const articles = await newsService.getArticlesByMatchId(matchId);
            commit(SET_ARTICLES, articles);
        }),
        getArticleBySlug: asyncErrorGuard(async ({ commit }: { commit: Commit }, slug: string) => {
            commit(SET_IS_SLUG_NEWS_FETCHED, false);
            const article = await newsService.getArticleBySlug(slug);
            if (article) {
                commit(SET_FULL_ARTICLE, article);
            }
            commit(SET_IS_SLUG_NEWS_FETCHED, true);
        }),
        getSocialAnalysesFlag: asyncErrorGuard(async ({ commit }: { commit: Commit }) => {
            if (!isSocialEnabled()) {
                commit(SET_IS_SOCIAL_ANALYSES_ENABLED, false);
                return;
            }

            const areSocialAnalysesEnabled = await featureFlagService.getFeatureValue('super-social.web-social-analyses', {
                is_analysis_enabled: 'false',
                is_writing_enabled: 'false',
                label_new: 'false',
            });
            commit(SET_IS_SOCIAL_ANALYSES_ENABLED, areSocialAnalysesEnabled.is_analysis_enabled === 'true');
        }),
    },
    getters: {
        areArticlesLoading: createSimpleGetter<NewsState, 'areArticlesLoading'>('areArticlesLoading'),
        areFirstArticlesFetched: createSimpleGetter<NewsState, 'areFirstArticlesFetched'>('areFirstArticlesFetched'),
        areAllArticlesFetched: createSimpleGetter<NewsState, 'areAllArticlesFetched'>('areAllArticlesFetched'),
        articles: createSimpleGetter<NewsState, 'articles'>('articles'),
        isSlugNewsFetched: createSimpleGetter<NewsState, 'isSlugNewsFetched'>('isSlugNewsFetched'),
        hasArticleMatchId: createSimpleGetter<NewsState, 'isSlugNewsFetched'>('isSlugNewsFetched'),
        areSocialAnalysesEnabled: createSimpleGetter<NewsState, 'areSocialAnalysesEnabled'>('areSocialAnalysesEnabled'),
        availableDates: (store: NewsState, getters: any) => {
            const dates = Object.keys(getters.articlesByDate).map(parseDate);
            return _orderBy(dates, (date) => date.getTime(), ['desc']);
        },
        articlesByDate: (store: NewsState) => {
            return _groupBy(store.articles, (article) => slugifyDate(article.datePublished));
        },
        articlesBySlug(store: NewsState) {
            return _keyBy(store.fullArticles, 'slug');
        },
    },
};
